<script lang="ts" setup>
import { v4 as uuid } from 'uuid'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

defineProps<{
  config: any
  input: string
  skeleton: boolean
  fetching: boolean
  ending: boolean
  error: any
  passcode: any
  result: any[]
  setInfiniteRef: (el: HTMLElement | null) => void
  onClear: () => void
  onSearch: () => void
  gotoBack: () => void
  gotoPasscodeBook: (passcode: any) => void
  onTrackGotoBook: (bookId: string) => boolean
}>()

const emit = defineEmits<{
  (event: 'update:input', value: string): void
}>()

function onInput(event: Event) {
  emit('update:input', (event.target as HTMLInputElement).value)
}
</script>

<template>
  <div :ref="setInfiniteRef" class="novelyn-search-page px-15px py-10px h-100dvh bg-n-bg-global overflow-scroll">
    <div class="novelyn-search-head w-full flex items-center space-x-5">
      <div class="relative flex-1 flex items-center space-x-5">
        <i-assets-icon-search class="w-4 h-4 absolute left-3 top-2.5 text-n-text-3" />
        <input
          :value="input" enterkeyhint="search"
          class="ml0! flex-1 h-9 pl-9 pr-4 rounded-full border-0 !outline-0 truncate caret-n-primary text-n-text-1"
          :style="{ backgroundColor: config.colors.inputBg }"
          :placeholder="config.texts.placeholder" autofocus
          @input="onInput"
          @keyup.enter="onSearch"
        >
        <div v-show="input.length" class="absolute right-3 top-2.5" @click="onClear">
          <i-assets-icon-close class="w-4 h-4 text-n-text-1" />
        </div>
      </div>
      <span class="text-14px text-n-primary font-bold" @click="gotoBack()">
        {{ config.texts.cancel }}
      </span>
    </div>

    <div v-if="skeleton">
      <div class="pt-20vh flex justify-center">
        <BasicSpinner class="mx-auto" />
      </div>
      <p class="mt-4 text-center font-bold">
        {{ config.texts.loading }}
      </p>
    </div>
    <div v-else-if="error" class="mt-5">
      <BizBlockError :title="error.msg" />
    </div>
    <div v-else-if="!passcode && !result.length">
      <BizBlockEmpty :title="config.texts.noResults" />
    </div>
    <template v-else>
      <div v-if="passcode" class="mt-5">
        <div class="mt-15px flex items-center space-x-15px">
          <img class="w-18 h-25 rounded-3px" :src="passcode.contentCoverUrl" alt="图片:书封">
          <div>
            <p class="text-15px font-bold text-n-text-1">
              {{ passcode.contentName }}
            </p>
            <p class="text-13px mt-10px text-n-text-2 line-clamp-2">
              {{ passcode.introduce }}
            </p>
            <a
              class="inline-block mt-1.5 px-10px py-3px text-12px lh-17px text-white rounded-full"
              :style="{ background: config.colors.startReadingBg }"
              @click="gotoPasscodeBook(passcode)"
            >
              {{ config.texts.startReading }}
            </a>
          </div>
        </div>
      </div>

      <div v-if="result.length" class="novelyn-search-results mt-5 -mb-5px">
        <div v-for="item in result" :key="item.id">
          <router-link
            v-slot="{ navigate }" custom
            :to="{ name: READER_ROUTE_NAME, params: { contentId: item.id, chapterId: item.firstChapter?.id || '' } }"
          >
            <div class="novelyn-book-row flex items-center space-x-15px" @click="onTrackGotoBook(item.id) && navigate($event)">
              <BizBookCover
                :id="uuid()"
                :src="item.contentCoverUrl || ''"
                :extra="{
                  author: item.authorInfo.author,
                  ...item,
                }"
                class="w-18 rounded-3px"
              />
              <div class="flex-1">
                <p class="text-15px font-bold line-clamp-1 text-n-text-1">
                  {{ item.contentName }}
                </p>
                <p class="text-13px mt-10px text-n-text-2 line-clamp-2">
                  {{ item.introduce }}
                </p>
                <div class="mt-10px flex justify-between text-13px text-n-text-3">
                  <p>
                    <span>{{ item.finishStatus ? config.texts.completed : config.texts.ongoing }}</span>
                    <span v-if="item.tagList[0]"> | </span>
                    <span>{{ item.tagList[0]?.name }}</span>
                  </p>
                </div>
              </div>
            </div>
          </router-link>
        </div>
      </div>

      <div v-if="ending" class="py-15px text-13px text-n-text-3 text-center">
        {{ config.texts.noMore }}
      </div>
      <div v-else-if="fetching" class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-3">
        <div class="animate-spin">
          <i-icon-park-outline-loading />
        </div>
        <p>{{ config.texts.loading }}</p>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.novelyn-search-page {
  padding: 18px 16px 32px;
  color: var(--novelyn-ink);
  background: var(--novelyn-bg);
}

.novelyn-search-head {
  position: sticky;
  top: 0;
  z-index: 5;
  padding: 2px 0 12px;
  background: var(--novelyn-bg);

  input {
    height: 42px;
    border: 1.5px solid var(--novelyn-rose) !important;
    box-shadow: 0 6px 16px rgba(224, 90, 126, .15) !important;
  }
}

.novelyn-search-results {
  padding: 2px 14px;
  border-radius: 20px;
  background: #fff;
  box-shadow: var(--novelyn-shadow-sm);
}

.novelyn-book-row {
  min-height: 124px;
  padding: 12px 0;
  border-top: 1px solid var(--novelyn-line);

  &:first-child {
    border-top: 0;
  }
}
</style>
